import { addDays, format, isSameDay } from 'date-fns';

import { EventAPIData, MeetingAPIData } from '../../../../types/growth-api';
import { CategoryDTO } from './category';
import { EventDTO } from './event';
import { SuggestedTopicDTO } from './suggested-topic';
import { UserDTO } from './user';

interface ScheduledMeetingInterface {
	id?: string;
	duration?: number;
	start_time?: Date | string | null;
	startTime?: Date | string | null;
	end_time?: Date | string | null;
	endTime?: Date | string | null;
	event?: EventDTO | EventAPIData | null;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function coerceDate(date: any) {
	if (date === null || date === undefined) return null;
	// make a date out of whatever was passed
	date = new Date(date);
	// epoch start (0) is falsey
	// invalid times (NaN) are falsey
	if (date.getTime() === 0) return null;
	if (Number.isNaN(Number(date))) return null;
	return date;
}

export class MeetingDTO {
	static readonly FILTER_STATUS_COMPLETE = 'complete';
	static readonly TIMELINE_STATUS_UPCOMING = 'upcoming';
	static readonly TIMELINE_STATUS_ACTIVE = 'active';
	static readonly TIMELINE_STATUS_PAST = 'past';

	static getNormalizedTimes(meeting: ScheduledMeetingInterface) {
		const duration = meeting.duration || 0;
		const startTime = coerceDate(meeting.startTime || meeting.start_time);
		const endTime = duration ? new Date(startTime.getTime() + Number(duration)) : coerceDate(meeting.endTime || meeting.end_time);
		const usedDuration = duration || (endTime && startTime ? endTime.getTime() - startTime.getTime() : 0);
		return {
			startTime: startTime as Date,
			duration: usedDuration as number,
			endTime: new Date(startTime.getTime() + Number(usedDuration)) as Date,
		};
	}

	createdAt: Date;
	updatedAt: Date;
	deletedAt: Date | null;

	collaborativeDocumentId?: string;
	alignmentCategory?: CategoryDTO;
	chatter?: Array<{ server_time: number; user_id: string; text: string }>;
	companyId: string;
	documentName: string;
	editedAgenda?: string;
	editedBrief?: string;
	event?: EventDTO;
	eventId: string;
	feedbackCategory?: CategoryDTO;
	generalCategory?: CategoryDTO;
	goalCategory?: CategoryDTO;
	guest?: UserDTO;
	guestId: string;
	host?: UserDTO;
	hostId: string;
	id: string;
	previousMeetingId?: string;
	priorityCategory?: CategoryDTO;
	pulseCategory?: CategoryDTO;
	recapCategory?: CategoryDTO;
	relationshipCategory?: CategoryDTO;
	smallSummary?: string;
	sortedAgenda?: string[];
	status: string;
	suggestedTopics?: SuggestedTopicDTO[];
	summarizingAt?: Date;
	summarizedAt?: Date;
	summarizedAgenda?: any[];
	summarizedBrief?: string;
	summarizedRecognition?: any[];
	timelineStatus: string;
	transcriptText?: string;
	transcriptUserId?: string;
	versionFlag: number;
	winsCategory?: CategoryDTO;
	apiData: MeetingAPIData;

	timeData?: any;

	constructor(data: MeetingAPIData | MeetingDTO) {
		data = data instanceof MeetingDTO ? data.apiData : data;
		this.apiData = data;
		this.timeData = MeetingDTO.getNormalizedTimes(data);

		this.createdAt = new Date(data.created_at);
		this.updatedAt = new Date(data.updated_at);
		this.event = data.event ? new EventDTO(data.event) : undefined;
		this.deletedAt = data.deleted_at ? new Date(data.deleted_at) : null;

		this.collaborativeDocumentId = data.collaborative_document_id;
		this.alignmentCategory = data.alignment_category ? new CategoryDTO(data.alignment_category) : undefined;
		this.chatter = data.chatter;
		this.companyId = data.company_id;
		this.documentName = data.document_name;
		this.editedAgenda = data.edited_agenda;
		this.editedBrief = data.edited_brief;
		this.eventId = data.event_id;
		this.feedbackCategory = data.feedback_category ? new CategoryDTO(data.feedback_category) : undefined;
		this.generalCategory = data.general_category ? new CategoryDTO(data.general_category) : undefined;
		this.goalCategory = data.goal_category ? new CategoryDTO(data.goal_category) : undefined;
		this.guestId = data.guest_id;
		this.hostId = data.host_id;
		this.id = data.id;
		this.previousMeetingId = data.previous_meeting_id;
		this.priorityCategory = data.priority_category ? new CategoryDTO(data.priority_category) : undefined;
		this.pulseCategory = data.pulse_category ? new CategoryDTO(data.pulse_category) : undefined;
		this.recapCategory = data.recap_category ? new CategoryDTO(data.recap_category) : undefined;
		this.relationshipCategory = data.relationship_category ? new CategoryDTO(data.relationship_category) : undefined;
		this.smallSummary = data.small_summary;
		this.sortedAgenda = data.sorted_agenda;
		this.status = data.status;
		this.suggestedTopics = data.suggested_topics ? data.suggested_topics.map((suggestedTopic) => new SuggestedTopicDTO(suggestedTopic)) : undefined;
		this.summarizedAgenda = data.summarized_agenda;
		this.summarizedAt = data.summarized_at ? new Date(data.summarized_at) : undefined;
		this.summarizedBrief = data.summarized_brief;
		this.summarizedRecognition = data.summarized_recognition || [];
		this.summarizingAt = data.summarizing_at ? new Date(data.summarizing_at) : undefined;
		this.timelineStatus = data.timeline_status;
		this.transcriptText = data.transcript_text;
		this.transcriptUserId = data.transcript_user_id;
		this.versionFlag = data.version_flag;
		this.winsCategory = data.wins_category ? new CategoryDTO(data.wins_category) : undefined;
	}

	get startTime(): Date {
		return this.timeData.startTime;
	}

	get endTime(): Date {
		return this.timeData.endTime;
	}

	get duration(): number {
		return this.timeData.duration;
	}

	get weekNumber() {
		const startTimeInt = this.startTime.getTime();
		const yearInt = this.startTime.getFullYear();
		const startOfYear = new Date(yearInt, 0, 1);
		const yearTimeInt = startTimeInt - startOfYear.getTime();
		const weekNumber = Math.ceil(yearTimeInt / (7 * DAY));
		return weekNumber;
	}

	formatStartsAt(formatString: string) {
		return format(this.startTime, formatString);
	}

	isWithPreviousManager<T extends { id: string; manager_id: string }>(user: T) {
		return this.isUserGuest(user) && user.manager_id !== this.hostId;
	}

	isActive() {
		return this.timelineStatus === MeetingDTO.TIMELINE_STATUS_ACTIVE;
	}

	isUpcoming() {
		return this.timelineStatus === MeetingDTO.TIMELINE_STATUS_UPCOMING;
	}

	isPast() {
		return this.timelineStatus === MeetingDTO.TIMELINE_STATUS_PAST;
	}

	isCurrent() {
		return this.isActive() || this.isUpcoming();
	}

	isToday() {
		const now = new Date();
		return isSameDay(now, this.startTime);
	}

	isTomorrow() {
		const tomorrow = addDays(new Date(), 1);
		return isSameDay(tomorrow, this.startTime);
	}

	isPastDate() {
		const then = this.startTime;
		const now = new Date();
		return !isSameDay(then, now) && then < now;
	}

	isTodayDate() {
		const then = this.startTime;
		const now = new Date();
		return isSameDay(then, now);
	}

	isFutureDate() {
		const then = this.startTime;
		const now = new Date();
		return then > now;
	}

	isSummarizing() {
		const summarizingAt = this.summarizingAt;
		const summarizedAt = this.summarizedAt;
		if (summarizingAt && summarizedAt) return Number(summarizingAt) > Number(summarizedAt);
		if (summarizingAt) return true;
		return false;
	}

	isSummarized() {
		if (!this.isSummarizing()) {
			if (this.summarizedAt) return true;
			if (this.summarizedBrief) return true;
		}
		return false;
	}

	hasWriteAccess(userId: string) {
		return this.isUserHost({ id: userId }) || this.isUserGuest({ id: userId });
	}

	isEventCanceled() {
		return this.event?.isCanceled() || false;
	}

	isUserHost<T extends { id: string }>(user: T) {
		return user.id === this.hostId;
	}

	isUserGuest<T extends { id: string }>(user: T) {
		return user.id === this.guestId;
	}

	isUserParticipant<T extends { id: string }>(user: T) {
		return user.id === this.hostId || user.id === this.guestId;
	}

	hasTranscript() {
		return !!this.transcriptText;
	}
}
